import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../component/PageHeader";
import ResponsivePagination from "react-responsive-pagination";
import { AddAllowanceModal } from "../component/AddAllowanceModal";
import { ImportAllowanceModal } from "../component/ImportAllowanceModal";
import { useWeb3ModalProvider } from "@web3modal/ethers/react";
import { useWallletContext } from "../hook/useWalletContext";
import { useRecoilState } from "recoil";
import { ShowProgress } from "../states/ShowProgress";
import { BrowserProvider } from "ethers";
import { REQUEST_TYPE, TOAST_ID } from "../common/const";
import { AllowanceService } from "../openapi";
import { toast } from "react-toastify";
import dateFormat from "dateformat";

const PAGE_LIMIT = 10;

export const Allowance = () => {
  const { apy } = useParams();

  const { walletProvider } = useWeb3ModalProvider();

  const { theme } = useWallletContext();

  const [, setShowProgress] = useRecoilState(ShowProgress);

  const [address, setAddress] = useState<string>("");
  const [createdAtFrom, setCreatedFrom] = useState<Date | null>();
  const [createdAtTo, setCreatedAtTo] = useState<Date | null>();

  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  const [allowances, setAllowances] = useState<any[]>([]);

  const [showAddAllowanceModal, setShowAddAllowanceModal] =
    useState<boolean>(false);
  const [showImportAllowanceModal, setShowImportAllowanceModal] =
    useState<boolean>(false);

  useEffect(() => {
    getAllowance();
  }, [page, apy]);

  const getAllowance = () => {
    setShowProgress(true);
    AllowanceService.listAllowances(
      parseInt(apy ?? "0"),
      (page - 1) * PAGE_LIMIT,
      PAGE_LIMIT,
      address,
      createdAtFrom?.getTime() ?? undefined,
      createdAtTo?.getTime() ?? undefined
    )
      .then((res) => {
        setShowProgress(false);

        setAllowances(res[0]);
        setTotal(res[1]);
      })
      .catch((err) => {
        setShowProgress(false);

        toast.error("Getting allowance has been failed.", {
          theme: theme,
          toastId: TOAST_ID.GETTING_ALLOWANCE,
        });
        console.log(err);
      });
  };

  const handleRemove = async (id: number) => {
    try {
      const provider = new BrowserProvider(walletProvider!);
      const signer = await provider.getSigner();

      setShowProgress(true);

      const signedMessage = await signer.signMessage(
        JSON.stringify({
          id: id,
          requestType: REQUEST_TYPE.REMOVE_ALLOWANCE,
        })
      );

      AllowanceService.removeAllowance({
        id: id,
        requestType: REQUEST_TYPE.REMOVE_ALLOWANCE,
        signedMessage: signedMessage,
      })
        .then((res) => {
          setShowProgress(false);

          if (res === "true") {
            toast.success("Removing allowance has been successful.", {
              theme: theme,
            });
            getAllowance();
          } else {
            toast.error("Removing allowance has been failed.", {
              theme: theme,
            });
          }
        })
        .catch((err) => {
          setShowProgress(false);
          toast.error("Removing allowance has been failed.", { theme: theme });
        });
    } catch (err) {
      console.log(err);
      toast.error("Removing allowance has been failed.", { theme: theme });
    }
  };

  return (
    <>
      <PageHeader
        title={`Allowance (APY : ${apy})`}
        buttonGroup={
          <>
            <button
              type="button"
              className="ti-btn ti-btn-success"
              data-hs-overlay="#hs-basic-modal"
              onClick={() => setShowImportAllowanceModal(true)}
            >
              <i className="ti ti-file-import margin-inline-end-initial" />
              Import
            </button>
            <button
              type="button"
              className="ti-btn ti-btn-primary ml-3"
              onClick={() => setShowAddAllowanceModal(true)}
            >
              <i className="ti ti-circle-plus margin-inline-end-initial" />
              Add
            </button>
          </>
        }
      />

      <div className="box">
        <div className="box-body">
          <div className="flex">
            <div className="block">
              <label
                htmlFor="inline-input-label-with-helper-text"
                className="ti-form-label text-md"
              >
                Address
              </label>
              <input
                type="text"
                id="inline-input-label-with-helper-text"
                className="ti-form-input"
                placeholder="Input the address"
                aria-describedby="hs-inline-input-helper-text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="flex mx-auto">
              <div className="block mr-3">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label text-md"
                >
                  Created At
                </label>
                <div className="flex rounded-sm overflow-auto">
                  <div className="px-4 inline-flex items-center min-w-fit ltr:rounded-l-sm rtl:rounded-r-sm border ltr:border-r-0 rtl:border-l-0 border-gray-200 bg-gray-50 dark:bg-black/20 dark:border-white/10">
                    <span className="text-sm text-gray-500 dark:text-white/70">
                      <i className="ri ri-calendar-line"></i>
                    </span>
                  </div>

                  <DatePicker
                    className="ti-form-input ltr:rounded-l-none rtl:rounded-r-none focus:z-10"
                    selected={createdAtFrom}
                    onChange={(date: Date | null) => setCreatedFrom(date)}
                    timeInputLabel="Time:"
                    dateFormat="MM/dd/yyyy h:mm aa"
                    showTimeInput
                  />
                </div>
              </div>
              <div className="flex">
                <label
                  htmlFor="inline-input-label-with-helper-text"
                  className="ti-form-label text-md mt-auto mb-3"
                >
                  ~
                </label>
              </div>
              <div className="flex ml-3">
                <div className="flex rounded-sm overflow-auto mt-auto">
                  <div className="px-4 inline-flex items-center min-w-fit ltr:rounded-l-sm rtl:rounded-r-sm border ltr:border-r-0 rtl:border-l-0 border-gray-200 bg-gray-50 dark:bg-black/20 dark:border-white/10">
                    <span className="text-sm text-gray-500 dark:text-white/70">
                      <i className="ri ri-calendar-line"></i>
                    </span>
                  </div>

                  <DatePicker
                    className="ti-form-input ltr:rounded-l-none rtl:rounded-r-none focus:z-10"
                    selected={createdAtTo}
                    onChange={(date: Date | null) => setCreatedAtTo(date)}
                    timeInputLabel="Time:"
                    dateFormat="MM/dd/yyyy h:mm aa"
                    showTimeInput
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              className="ti-btn ti-btn-primary h-46 mt-auto"
              onClick={() => {
                if (page === 1) {
                  getAllowance();
                } else {
                  setPage(1);
                }
              }}
            >
              Search
            </button>
          </div>
          <div className="mt-4 overflow-auto">
            <table className="ti-custom-table ti-custom-table-head">
              <thead className="">
                <tr>
                  <th scope="col text-center">No</th>
                  <th scope="col text-center">Address</th>
                  <th scope="col text-center">CreatedAt</th>
                  <th scope="col text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {allowances.map((item, index) => {
                  return (
                    <tr key={index}>
                      <td className="text-center">#{index + 1}</td>
                      <td className="text-center">{item.account}</td>
                      <td className="text-center">
                        {dateFormat(item.createdAt, "yyyy-mm-dd HH:MM:ss")}
                      </td>
                      <td className="text-center font-medium space-x-2 rtl:space-x-reverse">
                        <div className="hs-tooltip ti-main-tooltip">
                          <Link
                            to="#"
                            className="m-0 hs-tooltip-toggle relative w-8 h-8 ti-btn rounded-full p-0 transition-none focus:outline-none ti-btn-soft-danger"
                            onClick={() => handleRemove(item.id)}
                          >
                            <i className="ti ti-trash margin-inline-end-initial"></i>
                            <span
                              className="hs-tooltip-content ti-main-tooltip-content py-1 px-2 bg-gray-900 text-xs font-medium text-white shadow-sm dark:bg-slate-700"
                              role="tooltip"
                            >
                              Delete
                            </span>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex mt-2">
            <div className="ml-auto w-500">
              <ResponsivePagination
                current={page}
                total={Math.ceil(total / PAGE_LIMIT)}
                onPageChange={(page) => setPage(page)}
              />
            </div>
          </div>
        </div>
      </div>

      <AddAllowanceModal
        apy={parseInt(apy ?? "0")}
        open={showAddAllowanceModal}
        handleOpen={setShowAddAllowanceModal}
        handleRefresh={getAllowance}
      />
      <ImportAllowanceModal
        apy={parseInt(apy ?? "0")}
        open={showImportAllowanceModal}
        handleOpen={setShowImportAllowanceModal}
        handleRefresh={getAllowance}
      />
    </>
  );
};
